import { BRAND, SITE_ORIGIN } from "../brand";
import { LegalPage } from "../components/marketing-page";
import { marketingLinks, socialImageMeta } from "../components/marketing/layout";

export const links = marketingLinks;

export const meta = () => [
  { title: `Privacy Policy — ${BRAND.name}` },
  {
    name: "description",
    content: `Read how ${BRAND.name} handles merchant and product data while creating FDA-style Nutrition Facts and Supplement Facts panels in Shopify.`,
  },
  { name: "robots", content: "index,follow" },
  { tagName: "link", rel: "canonical", href: `${SITE_ORIGIN}/privacy` },
  { property: "og:url", content: `${SITE_ORIGIN}/privacy` },
  { property: "og:type", content: "website" },
  ...socialImageMeta(),
  {
    "script:ld+json": {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: `Privacy Policy — ${BRAND.name}`,
      url: `${SITE_ORIGIN}/privacy`,
      publisher: { "@type": "Organization", name: "Legitimate LLC" },
    },
  },
];

export default function Privacy() {
  return (
    <LegalPage title={`Privacy Policy — ${BRAND.name}`}>
      {/* WHO WE ARE */}
      <h2>Who we are</h2>
      <p>
        {BRAND.name} ("we", "our", "us") is a Shopify app published by
        Legitimate LLC. Our app lets merchants generate FDA-style US Nutrition
        Facts and Supplement Facts label panels from nutrition data they enter
        (labels are formatting aids, not a compliance guarantee — merchants
        should verify final labels with a qualified expert), stores those
        labels on the merchant's own Shopify store as metaobjects, and renders
        a live preview inside the Shopify admin. This Privacy Policy describes
        what data we collect, how we use it, and the rights you have over it.
      </p>

      {/* DATA COLLECTED */}
      <h2>Data we collect from merchants who install our app</h2>
      <p>
        We collect only what is necessary to operate the service:
      </p>
      <ul>
        <li>
          <strong>Shop identity.</strong> Your{" "}
          <code>myshopify.com</code> domain and store name.
          Used to associate your session with the correct store and to comply
          with Shopify's mandatory data-deletion (GDPR) webhooks.
        </li>
        <li>
          <strong>Shopify access / session tokens.</strong> When you install
          the app and grant OAuth access, Shopify issues us an access token.
          We store this token <strong>encrypted</strong> in our database and
          use it solely to call the Shopify Admin API on your behalf (for
          example, to read product titles and to write label metaobjects to
          your store). Tokens are never transmitted to third parties other
          than Shopify.
        </li>
        <li>
          <strong>Product references.</strong> The Shopify product IDs and
          titles for products you choose to label. These are stored in our
          database as a lightweight activity log that powers the in-app label
          history list.
        </li>
        <li>
          <strong>Nutrition data you enter.</strong> Serving size, servings
          per container, and all nutrient amounts (calories, total fat,
          saturated fat, trans fat, cholesterol, sodium, total carbohydrate,
          dietary fiber, total sugars, added sugars, protein, and any
          micronutrients you declare). This data is stored primarily as an
          app-owned metaobject <em>on your own Shopify store</em> — you own
          it and can delete it at any time through Shopify's admin or by
          uninstalling the app. We also keep a small activity record in our
          database (timestamp + product reference) so your history list
          survives reinstalls.
        </li>
      </ul>

      {/* DATA NOT COLLECTED */}
      <h2>Data we do NOT collect</h2>
      <ul>
        <li>
          <strong>End-customer / buyer personal data.</strong> {BRAND.name}{" "}
          is a merchant-facing tool. We never read, receive, or store any data
          about your end customers or shoppers.
        </li>
        <li>
          <strong>Behavioral or tracking data.</strong> We do not collect IP
          addresses, browser cookies, device fingerprints, analytics events,
          or any form of passive user tracking.
        </li>
        <li>
          <strong>Payment information.</strong> The app is currently free;
          no billing or payment data is collected.
        </li>
      </ul>

      {/* HOW WE USE IT */}
      <h2>How we use the data we collect</h2>
      <p>
        We use the data above solely to operate the label editor and storage
        functionality you installed {BRAND.name} for. Specifically:
      </p>
      <ul>
        <li>
          Authenticate your Shopify admin session so the app loads securely
          inside the Shopify admin iframe.
        </li>
        <li>
          Read your product list so you can select which product to label.
        </li>
        <li>
          Apply FDA 21 CFR 101.9 rounding and formatting rules to the
          nutrition values you provide and render a live Nutrition Facts panel
          preview.
        </li>
        <li>
          Save the calculated label as a metaobject on your store and display
          your label history.
        </li>
      </ul>
      <p>
        We <strong>never</strong> sell your data. We never use it for
        advertising. We never share it with third parties except the
        subprocessors listed below, and only to operate the service.
      </p>

      {/* SUBPROCESSORS */}
      <h2>Subprocessors</h2>
      <p>
        We share data with the following processors strictly to operate the
        service. These are the <em>only</em> third parties we use:
      </p>
      <ul>
        <li>
          <strong>Shopify</strong> — platform host, OAuth, and the store where
          your metaobject label data lives. Shopify's own privacy policy
          governs data on your store.
        </li>
        <li>
          <strong>Railway</strong> — our application hosting provider and
          managed Postgres database. Activity log records (shop identity,
          product references, timestamps) are stored in Railway's managed
          Postgres. Railway encrypts data at rest.
        </li>
      </ul>

      {/* RETENTION + DELETION */}
      <h2>Retention and deletion</h2>
      <div className="nm-callout">
        <p>
          <strong>Most label data lives on your own store.</strong> Because
          label content is stored as Shopify metaobjects, you can delete it at
          any time through the Shopify admin or by using the in-app "Remove all
          data" control — no request to us needed.
        </p>
      </div>
      <p>
        Activity log records we hold in our database are kept for the lifetime
        of your install plus a 30-day reinstall grace window. After 30 days of
        being uninstalled — or upon receipt of a Shopify{" "}
        <code>shop/redact</code> webhook — we hard-delete every
        record we hold for your shop from our database.
      </p>
      <p>
        We automatically honor all three of Shopify's mandatory data-deletion
        webhooks:
      </p>
      <ul>
        <li>
          <code>customers/data_request</code> — we respond
          within 30 days with any buyer-associated data we hold (in practice,
          none, because we do not collect buyer data).
        </li>
        <li>
          <code>customers/redact</code> — we delete any
          customer-associated records we hold within 30 days.
        </li>
        <li>
          <code>shop/redact</code> — we hard-delete all records
          for the shop from our database within 30 days.
        </li>
      </ul>

      {/* GDPR/CCPA */}
      <h2>Your rights (GDPR / CCPA)</h2>
      <p>
        Merchants and their personnel have the right to access, correct,
        export, or delete personal data we hold about them. To exercise any of
        these rights, email{" "}
        <a href={`mailto:${BRAND.supportEmail}`}>
          {BRAND.supportEmail}
        </a>{" "}
        with a description of your request. We will respond within 30 days.
      </p>
      <p>
        California residents may also have additional rights under the CCPA,
        including the right to know what categories of personal information we
        collect, the right to opt out of sale (we do not sell personal
        information), and the right to non-discrimination for exercising these
        rights.
      </p>

      {/* SECURITY */}
      <h2>Security</h2>
      <p>
        All data is transmitted over TLS (HTTPS). Database storage is
        encrypted at rest by our hosting provider (Railway). Shopify access
        tokens are stored in encrypted form in our database — they are never
        logged, never stored in plaintext, and never transmitted outside the
        Shopify + Railway trust boundary. Every Shopify webhook we receive is
        HMAC-verified against your app's shared secret; tampered or forged
        payloads are rejected with a 401 before any processing occurs.
      </p>

      {/* CHILDREN */}
      <h2>Children's privacy</h2>
      <p>
        {BRAND.name} is a merchant tool for food and beverage businesses. It
        is not directed at children under 13, and we do not knowingly collect
        personal information from children.
      </p>

      {/* CHANGES */}
      <h2>Changes to this policy</h2>
      <p>
        We may update this policy from time to time to reflect changes in our
        practices, technology, legal requirements, or other factors. When we
        do, we will update the "Last updated" date at the top of the page. If
        you have questions about any changes, contact us at the address below.
      </p>

      {/* CONTACT */}
      <h2>Contact</h2>
      <p>
        Questions about this privacy policy or requests to exercise your data
        rights:
      </p>
      <p>
        <a href={`mailto:${BRAND.supportEmail}`}>
          {BRAND.supportEmail}
        </a>
        <br />
        Legitimate LLC — mailing address available on request.
      </p>
    </LegalPage>
  );
}
